/* eslint-disable class-methods-use-this */
import { firstValueFrom } from 'rxjs';

import { SimpleTreeManager } from './simple-tree-manager';
import { TreeBuilderStrategy } from './tree-core/tree-builder-strategy';
import { DropRequest, DropUIZone, TreeUIFacade } from './tree-core/tree-ui-facade';
import { TreeViewModel, FlatNode } from './tree-core/tree-view-model';
import { WorkPackageTreeNode } from './wp-tree-view.component';
import { UIDropRequest, WorkPackagesService } from 'src/app/services/work-package.service';

export class WpClientDrivenTreeFacade implements TreeUIFacade<WorkPackageTreeNode> {
  // 1. Our source of truth (Flat dictionary)
  private readonly rawData = new Map<string, WorkPackageTreeNode>();

  // 2. Core dependencies (will be recreated or repopulated on each regeneration)
  private treeManager: SimpleTreeManager<WorkPackageTreeNode>;
  private treeViewModel: TreeViewModel<WorkPackageTreeNode>;

  constructor(
    private readonly projectId: string,
    private readonly workPackagesService: WorkPackagesService,
    private readonly treeBuilder: TreeBuilderStrategy<WorkPackageTreeNode>,
  ) {
    this.treeManager = new SimpleTreeManager<WorkPackageTreeNode>();
    this.treeViewModel = new TreeViewModel<WorkPackageTreeNode>(this.treeManager);
  }

  //#region Initialization and Regeneration

  async initNodes(): Promise<void> {
    // Download ALL nodes from the backend in a single call (Eager Load)
    const allNodes = await firstValueFrom(
      this.workPackagesService.listWorkPackages({
        projectId: this.projectId,
        offset: 0,
        limit: 10000,
      }),
    );

    // Populate the cache
    this.rawData.clear();
    for (const node of allNodes.items) {
      this.rawData.set(node.id, node);
    }

    // Build the tree for the first time
    await this.regenerateTree();
  }

  /**
   * The "Engine" that transforms flat data into a structured tree
   * according to custom rules.
   */
  private async regenerateTree() {
    // 1. Save UI state
    const previouslyExpanded = this.treeViewModel ? this.getExpandedKeys() : new Set<string>();

    // 2. Prepare the manager by clearing it
    this.treeManager.clear();

    // 3. DELEGATE CONSTRUCTION TO THE STRATEGY
    await this.treeBuilder.build(this.rawData, this.treeManager);

    // 4. Restore UI state
    previouslyExpanded.forEach((key) => this.treeViewModel.expandNode(key));
  }

  // Helper method (to be added to TreeViewModel if it doesn't exist)
  private getExpandedKeys(): Set<string> {
    // Assumes that TreeViewModel exposes the expanded nodes, or they can be derived
    return new Set(
      this.treeViewModel
        .flattenNodes()
        .filter((n) => n.expanded)
        .map((n) => n.key),
    );
  }

  //#endregion

  //#region Reactive Updates (CRUD)

  async updateNodeHandler(id: string): Promise<boolean> {
    if (!this.rawData.has(id)) return false;

    // Download the new node data (or receive it from the payload)
    const updatedData = await firstValueFrom(this.workPackagesService.getWorkPackage({ id }));
    this.rawData.set(id, updatedData);

    // Regenerate the view applying the new rules (it might have changed name or parent)
    await this.regenerateTree();
    return true;
  }

  async createNodeHandler(data: WorkPackageTreeNode): Promise<boolean> {
    this.rawData.set(data.id, data);
    await this.regenerateTree();
    return true;
  }

  //#endregion

  //#region Reading and UI State

  getFlattenNodes(): FlatNode<WorkPackageTreeNode>[] {
    return this.treeViewModel.flattenNodes();
  }

  async nodeExpand(node: FlatNode<WorkPackageTreeNode>): Promise<void> {
    // Eager loading: we don't download anything, just change the visual state
    this.treeViewModel.expandNode(node.key);
  }

  nodeCollapse(node: FlatNode<WorkPackageTreeNode>): void {
    this.treeViewModel.collapseNode(node.key);
  }

  nodeCollapseHandler(key: string): void {
    this.treeViewModel.collapseNode(key);
  }

  //#endregion

  //#region Drag & Drop Disabled

  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  isDragZoneLegit(draggingKey: string, key: string) {
    return false;
  }
  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  buildDropRequest(draggingKey: string | undefined, dropZone: DropUIZone | undefined) {
    return undefined;
  }
  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  async dragEndHandler(request: DropRequest, resetDrag: () => void) {
    return false;
  }
  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  droppedNodeHandler(request: UIDropRequest) {
    throw new Error('Drag and drop is disabled.');
  }

  //#endregion
}
